import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaTimes, FaUser, FaMapMarker, FaBriefcase, FaGraduationCap, FaHeart, FaVenusMars, FaCalendarAlt, FaIdCard, FaUsers, FaTree, FaSlidersH, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const SearchPage = () => {
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(true);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });
    const [filterOptions, setFilterOptions] = useState({
        genders: [],
        maritalStatus: [],
        caste: [],
        education: [],
        occupation: [],
        countries: [],
        states: [],
        cities: []
    });
    
    // Location states
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    
    // Multi-select states - Store as objects with value (the actual name) and label
    const [selectedCastes, setSelectedCastes] = useState([]);
    const [selectedEducations, setSelectedEducations] = useState([]);
    const [selectedOccupations, setSelectedOccupations] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedMaritalStatuses, setSelectedMaritalStatuses] = useState([]);
    
    // Dropdown values for selection
    const [tempCaste, setTempCaste] = useState('');
    const [tempEducation, setTempEducation] = useState('');
    const [tempOccupation, setTempOccupation] = useState('');
    const [tempCountry, setTempCountry] = useState('');
    const [tempMaritalStatus, setTempMaritalStatus] = useState('');
    
    const [filters, setFilters] = useState({
        gender: '',
        min_age: '',
        max_age: '',
        country_id: '',
        state_id: '',
        city_id: ''
    });

    const maritalStatusOptions = [
        { value: 'Unmarried', label: 'Unmarried', icon: '💑' },
        { value: 'Divorce', label: 'Divorced', icon: '💔' },
        { value: 'Widowed', label: 'Widow/Widower', icon: '🕊️' }
    ];

    useEffect(() => {
        fetchCountries();
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        const isSingleCountrySelected = selectedCountries.length === 1;
        if (isSingleCountrySelected && selectedCountries[0]) {
            fetchStates(selectedCountries[0].value);
            setFilters(prev => ({ ...prev, state_id: '', city_id: '' }));
        } else {
            setStates([]);
            setCities([]);
            setFilters(prev => ({ ...prev, state_id: '', city_id: '' }));
        }
    }, [selectedCountries]);

    useEffect(() => {
        if (filters.state_id && !filters.state_id.includes(',')) {
            fetchCities(filters.state_id);
            setFilters(prev => ({ ...prev, city_id: '' }));
        } else {
            setCities([]);
        }
    }, [filters.state_id]);

    useEffect(() => {
        searchUsers();
    }, [pagination.page, filters, selectedCastes, selectedEducations, selectedOccupations, selectedCountries, selectedMaritalStatuses]);

    const fetchCountries = async () => {
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/locations/countries');
            if (response.data.success) setCountries(response.data.data);
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    const fetchStates = async (countryId) => {
        setLoadingStates(true);
        try {
            const response = await axios.get(`https://app.bihebari.com.np/api/locations/states/${countryId}`);
            if (response.data.success) setStates(response.data.data);
        } catch (error) {
            console.error('Error fetching states:', error);
        } finally {
            setLoadingStates(false);
        }
    };

    const fetchCities = async (stateId) => {
        setLoadingCities(true);
        try {
            const response = await axios.get(`https://app.bihebari.com.np/api/locations/cities/${stateId}`);
            if (response.data.success) {
                setCities(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
        } finally {
            setLoadingCities(false);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/users/filter-options');
            if (response.data.success) {
                setFilterOptions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value });
        setPagination({ ...pagination, page: 1 });
    };

    const handleReset = () => {
        setFilters({
            gender: '',
            min_age: '',
            max_age: '',
            country_id: '',
            state_id: '',
            city_id: ''
        });
        setSelectedCastes([]);
        setSelectedEducations([]);
        setSelectedOccupations([]);
        setSelectedCountries([]);
        setSelectedMaritalStatuses([]);
        setTempCaste('');
        setTempEducation('');
        setTempOccupation('');
        setTempCountry('');
        setTempMaritalStatus('');
        setStates([]);
        setCities([]);
        setPagination({ ...pagination, page: 1 });
        toast.info('All filters reset');
    };

    const searchUsers = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            
            // Basic filters
            if (filters.gender) queryParams.append('gender', filters.gender);
            if (filters.min_age) queryParams.append('min_age', filters.min_age);
            if (filters.max_age) queryParams.append('max_age', filters.max_age);
            
            // IMPORTANT FIX: Send the actual caste NAMES, not IDs
            if (selectedCastes.length > 0) {
                const casteNames = selectedCastes.map(c => c.label).join(',');
                queryParams.append('caste', casteNames);
                console.log('Sending caste names:', casteNames);
            }
            
            // Education - send the actual education names
            if (selectedEducations.length > 0) {
                const educationNames = selectedEducations.map(e => e.label).join(',');
                queryParams.append('education', educationNames);
            }
            
            // Occupation - send the actual occupation names
            if (selectedOccupations.length > 0) {
                const occupationNames = selectedOccupations.map(o => o.label).join(',');
                queryParams.append('occupation', occupationNames);
            }
            
            // Marital Status - send the actual status values
            if (selectedMaritalStatuses.length > 0) {
                const maritalValues = selectedMaritalStatuses.map(m => m.value).join(',');
                queryParams.append('marital_status', maritalValues);
                console.log('Sending marital status:', maritalValues);
            }
            
            // Country filter
            if (selectedCountries.length > 0) {
                queryParams.append('country_id', selectedCountries.map(c => c.value).join(','));
            }
            
            // State and City filters
            if (selectedCountries.length === 1) {
                if (filters.city_id) {
                    // Get city name from cities array
                    const selectedCity = cities.find(c => c.id == filters.city_id);
                    if (selectedCity) {
                        queryParams.append('city_name', selectedCity.name);
                    }
                } else if (filters.state_id) {
                    queryParams.append('state_id', filters.state_id);
                }
            }
            
            queryParams.append('page', pagination.page);
            queryParams.append('limit', pagination.limit);
            
            console.log('Search Query Params:', queryParams.toString());
            
            const response = await axios.get(`https://app.bihebari.com.np/api/users/search/advanced?${queryParams}`);
            if (response.data.success) {
                setResults(response.data.data);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Error searching users:', error);
            toast.error('Error performing search');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPagination({ ...pagination, page: newPage });
        window.scrollTo(0, 0);
    };

    const getAge = (dob) => {
        if (!dob) return 'N/A';
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
        return age;
    };

    const isSingleCountrySelected = selectedCountries.length === 1;

    // Fix: When adding to multi-select, store the actual name/value properly
    const handleCasteSelect = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            // Find the caste from filterOptions
            const option = filterOptions.caste?.find(opt => opt.value === selectedValue);
            const label = option ? option.label : selectedValue;
            addToMultiSelect(selectedCastes, setSelectedCastes, selectedValue, label);
            setTempCaste('');
            e.target.value = '';
        }
    };

    const handleMaritalStatusSelect = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            const option = maritalStatusOptions.find(opt => opt.value === selectedValue);
            if (option) {
                addToMultiSelect(selectedMaritalStatuses, setSelectedMaritalStatuses, option.value, option.label);
            }
            setTempMaritalStatus('');
            e.target.value = '';
        }
    };

    const handleEducationSelect = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            let option = filterOptions.education?.find(opt => opt.value == selectedValue);
            const label = option ? option.label : selectedValue;
            addToMultiSelect(selectedEducations, setSelectedEducations, selectedValue, label);
            setTempEducation('');
            e.target.value = '';
        }
    };

    const handleOccupationSelect = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            let option = filterOptions.occupation?.find(opt => opt.value == selectedValue);
            const label = option ? option.label : selectedValue;
            addToMultiSelect(selectedOccupations, setSelectedOccupations, selectedValue, label);
            setTempOccupation('');
            e.target.value = '';
        }
    };

    const handleCountrySelect = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            const option = countries.find(opt => opt.id == selectedValue);
            const label = option ? option.name : selectedValue;
            addToMultiSelect(selectedCountries, setSelectedCountries, selectedValue, label);
            setTempCountry('');
            e.target.value = '';
        }
    };

    const addToMultiSelect = (array, setArray, value, label, max = 3) => {
        if (array.length >= max) {
            toast.warning(`You can only select up to ${max} values`);
            return false;
        }
        if (!array.find(item => item.value === value)) {
            setArray([...array, { value, label }]);
            return true;
        }
        return false;
    };

    const removeFromMultiSelect = (array, setArray, value) => {
        setArray(array.filter(item => item.value !== value));
    };

    const getMemberId = (userId) => `BB${userId}`;
    const maskName = (name) => {
        if (!name) return 'User';
        const firstLetter = name.charAt(0);
        const maskedPart = '*'.repeat(name.length - 1);
        return firstLetter + maskedPart;
    };

    const getPlaceholderImage = (gender) => {
        if (!gender) return '/user.png';
        const lowerGender = gender.toLowerCase();
        if (lowerGender === 'male') return '/male.png';
        if (lowerGender === 'female') return '/female.png';
        return '/user.png';
    };

    const formatGender = (gender) => {
        if (!gender) return 'Not specified';
        return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
    };

    const getEducationOptions = () => filterOptions.education?.length > 0 ? filterOptions.education : educationOptions;
    const getOccupationOptions = () => filterOptions.occupation?.length > 0 ? filterOptions.occupation : occupationOptions;

    // Education options fallback
    const educationOptions = [
        { value: 'High School', label: '🎓 High School' },
        { value: "Bachelor's Degree", label: "🎓 Bachelor's Degree" },
        { value: "Master's Degree", label: "🎓 Master's Degree" },
        { value: 'PhD', label: '🎓 PhD' },
        { value: 'Diploma', label: '📜 Diploma' }
    ];

    // Occupation options fallback
    const occupationOptions = [
        { value: 'Government Employee', label: '🏛️ Government Employee' },
        { value: 'Private Employee', label: '💼 Private Employee' },
        { value: 'Business', label: '🏢 Business' },
        { value: 'Doctor', label: '🏥 Doctor' },
        { value: 'Engineer', label: '🔧 Engineer' }
    ];

    // Function to open user details in new tab
    const openUserDetails = (userId) => {
        window.open(`/admin/users/${userId}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="min-h-screen py-8 px-4 bg-gradient-to-br from-rose-50 via-pink-50 to-indigo-50">
            <div className="max-w-7xl mx-auto">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="relative inline-block">
                        <div className="absolute -top-10 -left-10 w-20 h-20 bg-pink-200 rounded-full opacity-50 animate-pulse"></div>
                        <div className="absolute -bottom-10 -right-10 w-20 h-20 bg-indigo-200 rounded-full opacity-50 animate-pulse"></div>
                        <div className="flex items-center justify-center space-x-3 mb-4">
                            <div className="bg-gradient-to-r from-red-500 to-pink-500 p-3 rounded-full shadow-lg">
                                <FaHeart className="text-white text-3xl animate-pulse" />
                            </div>
                            <h1 className="text-5xl font-bold bg-gradient-to-r from-red-600 via-pink-600 to-indigo-600 bg-clip-text text-transparent">
                                Find Your Soulmate
                            </h1>
                            <div className="bg-gradient-to-r from-indigo-500 to-blue-500 p-3 rounded-full shadow-lg">
                                <FaHeart className="text-white text-3xl animate-pulse" />
                            </div>
                        </div>
                        <div className="h-1 w-32 mx-auto bg-gradient-to-r from-red-500 via-pink-500 to-indigo-500 rounded-full"></div>
                    </div>
                    <p className="text-gray-600 mt-4 text-lg">Discover your perfect match among thousands of verified profiles</p>
                </div>

                {/* Filter Toggle Button */}
                <div className="flex justify-center mb-6">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center space-x-3 px-8 py-3 bg-gradient-to-r from-red-600 to-indigo-600 text-white rounded-full hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                        {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                        <FaSlidersH />
                        <span className="font-semibold">{showFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}</span>
                    </button>
                </div>

                {/* Advanced Filters */}
                {showFilters && (
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 mb-8 border border-gray-100">
                        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-800 flex items-center">
                                <FaFilter className="mr-2 text-red-500" />
                                Advanced Search Filters
                            </h3>
                            <button onClick={handleReset} className="text-red-500 hover:text-red-700 flex items-center space-x-1 px-4 py-2 rounded-lg hover:bg-red-50 transition">
                                <FaTimes />
                                <span>Reset All</span>
                            </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Gender - Modern Select */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                                    <FaVenusMars className="mr-2 text-pink-500" /> Gender
                                </label>
                                <select name="gender" value={filters.gender} onChange={handleFilterChange} 
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all duration-200 bg-gray-50">
                                    <option value="">🌈 Any Gender</option>
                                    <option value="Male">👨 Male</option>
                                    <option value="Female">👩 Female</option>
                                    <option value="Other">🌈 Other</option>
                                </select>
                            </div>

                            {/* Marital Status - Multi Select */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">Marital Status (Max 3)</label>
                                <div className="flex flex-wrap gap-2 mb-2 min-h-[40px]">
                                    {selectedMaritalStatuses.map((item) => (
                                        <span key={item.value} className="bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 px-3 py-1 rounded-full text-sm flex items-center gap-2 shadow-sm">
                                            {item.label}
                                            <button onClick={() => removeFromMultiSelect(selectedMaritalStatuses, setSelectedMaritalStatuses, item.value)} className="hover:text-yellow-900">&times;</button>
                                        </span>
                                    ))}
                                </div>
                                <select className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" value={tempMaritalStatus} onChange={handleMaritalStatusSelect}>
                                    <option value="">💑 Select Marital Status</option>
                                    {maritalStatusOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.icon} {option.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Caste - Multi Select */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">Caste (Max 3)</label>
                                <div className="flex flex-wrap gap-2 mb-2 min-h-[40px]">
                                    {selectedCastes.map((item) => (
                                        <span key={item.value} className="bg-gradient-to-r from-red-100 to-red-200 text-red-800 px-3 py-1 rounded-full text-sm flex items-center gap-2 shadow-sm">
                                            {item.label}
                                            <button onClick={() => removeFromMultiSelect(selectedCastes, setSelectedCastes, item.value)} className="hover:text-red-900">&times;</button>
                                        </span>
                                    ))}
                                </div>
                                <select className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" value={tempCaste} onChange={handleCasteSelect}>
                                    <option value="">🏷️ Select Caste</option>
                                    {filterOptions.caste?.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Education - Multi Select */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                                    <FaGraduationCap className="mr-2 text-purple-500" /> Education (Max 3)
                                </label>
                                <div className="flex flex-wrap gap-2 mb-2 min-h-[40px]">
                                    {selectedEducations.map((item) => (
                                        <span key={item.value} className="bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 px-3 py-1 rounded-full text-sm flex items-center gap-2 shadow-sm">
                                            {item.label}
                                            <button onClick={() => removeFromMultiSelect(selectedEducations, setSelectedEducations, item.value)} className="hover:text-purple-900">&times;</button>
                                        </span>
                                    ))}
                                </div>
                                <select className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" value={tempEducation} onChange={handleEducationSelect}>
                                    <option value="">🎓 Select Education</option>
                                    {getEducationOptions().map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Occupation - Multi Select */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                                    <FaBriefcase className="mr-2 text-blue-500" /> Occupation (Max 3)
                                </label>
                                <div className="flex flex-wrap gap-2 mb-2 min-h-[40px]">
                                    {selectedOccupations.map((item) => (
                                        <span key={item.value} className="bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2 shadow-sm">
                                            {item.label}
                                            <button onClick={() => removeFromMultiSelect(selectedOccupations, setSelectedOccupations, item.value)} className="hover:text-blue-900">&times;</button>
                                        </span>
                                    ))}
                                </div>
                                <select className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" value={tempOccupation} onChange={handleOccupationSelect}>
                                    <option value="">💼 Select Occupation</option>
                                    {getOccupationOptions().map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Country - Multi Select */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                                    <FaMapMarker className="mr-2 text-teal-500" /> Country (Max 3)
                                </label>
                                <div className="flex flex-wrap gap-2 mb-2 min-h-[40px]">
                                    {selectedCountries.map((item) => (
                                        <span key={item.value} className="bg-gradient-to-r from-teal-100 to-teal-200 text-teal-800 px-3 py-1 rounded-full text-sm flex items-center gap-2 shadow-sm">
                                            {item.label}
                                            <button onClick={() => removeFromMultiSelect(selectedCountries, setSelectedCountries, item.value)} className="hover:text-teal-900">&times;</button>
                                        </span>
                                    ))}
                                </div>
                                <select className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" value={tempCountry} onChange={handleCountrySelect}>
                                    <option value="">🌍 Select Country</option>
                                    {countries.map((option) => (
                                        <option key={option.id} value={option.id}>{option.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* State - Conditional */}
                            {isSingleCountrySelected && (
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">State</label>
                                    <select name="state_id" value={filters.state_id} onChange={handleFilterChange} 
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" disabled={loadingStates}>
                                        <option value="">🗺️ Any State</option>
                                        {states.map(state => <option key={state.id} value={state.id}>{state.name}</option>)}
                                    </select>
                                    {loadingStates && <p className="text-xs text-gray-500">Loading states...</p>}
                                </div>
                            )}

                            {/* City - Dropdown based on selected state */}
                            {isSingleCountrySelected && filters.state_id && (
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">City</label>
                                    <select name="city_id" value={filters.city_id} onChange={handleFilterChange} 
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" disabled={loadingCities}>
                                        <option value="">🏙️ Any City</option>
                                        {cities.map(city => <option key={city.id} value={city.id}>{city.name}</option>)}
                                    </select>
                                    {loadingCities && <p className="text-xs text-gray-500">Loading cities...</p>}
                                </div>
                            )}

                            {/* Age Range */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                                    <FaCalendarAlt className="mr-2 text-green-500" /> Age Range
                                </label>
                                <div className="flex gap-3">
                                    <input type="number" name="min_age" value={filters.min_age} onChange={handleFilterChange} 
                                        placeholder="Min Age" className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" />
                                    <input type="number" name="max_age" value={filters.max_age} onChange={handleFilterChange} 
                                        placeholder="Max Age" className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all bg-gray-50" />
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex justify-center">
                            <button onClick={searchUsers} className="flex items-center space-x-3 px-10 py-3 bg-gradient-to-r from-red-600 via-pink-600 to-indigo-600 text-white rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                                <FaSearch className="text-lg" />
                                <span className="font-semibold">Search Profiles</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Results Count */}
                <div className="mb-6 flex justify-between items-center">
                    <div className="bg-white/80 backdrop-blur-sm rounded-full px-6 py-2 shadow-sm">
                        <p className="text-gray-700">Found <strong className="text-red-600 text-lg">{pagination.total}</strong> perfect matches</p>
                    </div>
                </div>

                {/* Search Results - Clickable cards that open in new tab */}
                {loading ? (
                    <div className="flex justify-center items-center h-96">
                        <div className="text-center">
                            <div className="relative">
                                <div className="w-20 h-20 border-4 border-red-200 rounded-full animate-spin border-t-red-600"></div>
                                <FaHeart className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-red-500 text-2xl animate-pulse" />
                            </div>
                            <p className="mt-4 text-gray-600 font-medium">Finding your soulmate...</p>
                        </div>
                    </div>
                ) : results.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-xl p-16 text-center">
                        <div className="w-32 h-32 bg-gradient-to-r from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FaHeart className="text-gray-400 text-5xl" />
                        </div>
                        <h3 className="text-2xl font-semibold text-gray-600 mb-2">No profiles found</h3>
                        <p className="text-gray-500">Try adjusting your search criteria to find more matches</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {results.map((user) => (
                            <div 
                                key={user.id} 
                                onClick={() => openUserDetails(user.id)}
                                className="group bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 cursor-pointer"
                            >
                                <div className="relative h-64 bg-gradient-to-r from-red-500 via-pink-500 to-indigo-600">
                                    <img src={getPlaceholderImage(user.gender)} alt={user.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                                    <div className="absolute bottom-0 left-0 right-0 p-5">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="text-white text-2xl font-bold">{maskName(user.name)}</h3>
                                            <div className="bg-gradient-to-r from-red-500 to-indigo-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-2 shadow-lg">
                                                <FaIdCard className="text-xs" />
                                                <span className="font-semibold">{getMemberId(user.id)}</span>
                                            </div>
                                        </div>
                                        <p className="text-white/90 text-sm flex items-center gap-2">
                                            <FaCalendarAlt className="text-pink-300" />
                                            {getAge(user.dob)} years • {formatGender(user.gender)}
                                        </p>
                                    </div>
                                    {user.pay_status === 'paid' && (
                                        <div className="absolute top-4 right-4 bg-green-500 text-white text-xs px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                            Verified
                                        </div>
                                    )}
                                </div>

                                <div className="p-5">
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                                            <div className="flex items-center text-gray-700">
                                                <FaUsers className="mr-2 text-red-500 w-4" />
                                                <span className="text-sm font-medium">Caste:</span>
                                                <span className="ml-2 text-sm">{user.caste || 'Not specified'}</span>
                                            </div>
                                            <div className="flex items-center text-gray-700">
                                                <FaTree className="mr-2 text-green-600 w-4" />
                                                <span className="text-sm font-medium">Gotra:</span>
                                                <span className="ml-2 text-sm">{user.gotra || 'Not specified'}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center text-gray-700">
                                            <FaBriefcase className="mr-2 text-blue-500 w-4 flex-shrink-0" />
                                            <span className="text-sm font-medium">Occupation:</span>
                                            <span className="ml-2 text-sm truncate">{user.occupation || 'Not specified'}</span>
                                        </div>
                                        
                                        <div className="flex items-center text-gray-700">
                                            <FaGraduationCap className="mr-2 text-purple-500 w-4 flex-shrink-0" />
                                            <span className="text-sm font-medium">Education:</span>
                                            <span className="ml-2 text-sm truncate">{user.education || 'Not specified'}</span>
                                        </div>

                                        <div className="pt-2 border-t border-gray-100">
                                            <div className="flex items-start text-gray-700">
                                                <FaMapMarker className="mr-2 text-teal-500 w-4 mt-0.5" />
                                                <div className="flex-1">
                                                    <span className="text-sm font-medium">Location:</span>
                                                    <span className="ml-2 text-sm">
                                                        {[user.city, user.state, user.country].filter(Boolean).join(', ') || 'Not specified'}
                                                    </span>
                                                    {user.country === 'Nepal' && user.district && (
                                                        <div className="text-xs text-gray-500 mt-1 ml-6">District: {user.district}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="flex justify-center space-x-3 mt-12">
                        <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} 
                            className="px-6 py-2 border-2 border-gray-300 rounded-full disabled:opacity-50 hover:bg-gradient-to-r hover:from-red-500 hover:to-indigo-500 hover:text-white hover:border-transparent transition-all duration-300">
                            Previous
                        </button>
                        <span className="px-6 py-2 bg-gradient-to-r from-red-500 to-indigo-500 text-white rounded-full font-semibold shadow-md">
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages} 
                            className="px-6 py-2 border-2 border-gray-300 rounded-full disabled:opacity-50 hover:bg-gradient-to-r hover:from-red-500 hover:to-indigo-500 hover:text-white hover:border-transparent transition-all duration-300">
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchPage;